import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FormikForm } from "components/FormikForm/FormikForm";
import {
  AnnotationDoc,
  DocumentAnnotationPanel,
  DocumentPin,
} from "components/DocumentAnnotation/DocumentAnnotationPanel";
import { SidebarQuestion } from "components/DocumentAnnotation/QuestionCard";
import { QuestionSidebar } from "components/DocumentAnnotation/QuestionSidebar";
import { CaretLeft } from "components/icons/CaretLeft";
import { TickSlim } from "components/icons/TickSlim";
import { Thread } from "components/icons/Thread";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useToast } from "hooks/useToast";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Info } from "components/icons/Info";
import { DISPLAY_DATE } from "constants/date";
import {
  useGetChatQNAQuery,
  useReviewChatQNAMutation,
  useSaveReviewProgressMutation,
} from "store/apis/chatQnA";
import { taskApis } from "store/apis/task";
import { debounce } from "utils/debouncing";
import { pluralize } from "utils/pluralize";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { FileObject } from "types/Models/fileObject";
import {
  REQUESTED_ANSWER_REVIEW_STATUS,
  RequestedAnswerReviewDecision,
} from "constants/requestedAnswerReview";
import { PreviousResponses } from "./PreviousResponses";
import { AcceptedResponses } from "./AcceptedResponses";
import { FilePreviewCell } from "./FilePreviewCell";

const REVIEW_NOTE_PLACEHOLDER = "Enter follow-up question here";
const REVIEW_PENDING_ERROR = "Review pending for this response!";

export const ReviewRequestAnswers = ({
  isOpen,
  close,
  requestId,
  groupId,
}: ModalProps & {
  requestId: string;
  groupId: string;
}) => {
  const { alertToast, successToast } = useToast();
  const dispatch = useDispatch();
  const isSmallScreen = useIsSmallScreen();

  const { data, isLoading, isSuccess } = useGetChatQNAQuery(
    { groupId, requestId },
    { skip: !groupId || !requestId }
  );

  const [reviewAnswers, { isLoading: isSubmitting }] =
    useReviewChatQNAMutation();
  const [saveReviewProgress] = useSaveReviewProgressMutation();

  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [activePinId, setActivePinId] = useState<string | null>(null);
  const [docViewerQuestionId, setDocViewerQuestionId] = useState<string | null>(
    null
  );
  const [hasAttemptedReview, setHasAttemptedReview] = useState(false);
  const [scrollToQuestionId, setScrollToQuestionId] = useState<string | null>(
    null
  );
  const [savingDecisionKey, setSavingDecisionKey] = useState<string | null>(
    null
  );
  const requestedAnswers = useMemo(
    () => data?.requested_answers ?? [],
    [data?.requested_answers]
  );

  const hasFollowUpRequested = requestedAnswers.some(
    ({ review_status }) =>
      review_status === REQUESTED_ANSWER_REVIEW_STATUS.FOLLOW_UP_REQUESTED
  );

  const isQuestionUnresolved = useCallback(
    ({
      review_status,
      follow_up_note,
    }: {
      review_status: string;
      follow_up_note: string | null;
    }) => {
      if (review_status === REQUESTED_ANSWER_REVIEW_STATUS.PENDING) {
        return true;
      }
      const isFollowUpSelected =
        review_status === REQUESTED_ANSWER_REVIEW_STATUS.FOLLOW_UP_REQUESTED;
      return isFollowUpSelected && !follow_up_note?.trim();
    },
    []
  );

  const errorQuestionIds = hasAttemptedReview
    ? requestedAnswers.filter(isQuestionUnresolved).map((a) => a.uuid)
    : [];

  const pendingReviewAnswers = requestedAnswers.filter(
    ({ review_status }) =>
      review_status === REQUESTED_ANSWER_REVIEW_STATUS.PENDING
  );

  const annotationDocs: AnnotationDoc[] = useMemo(
    () =>
      (data?.reference_documents ?? []).map((doc) => ({
        id: doc.file_uuid,
        name: doc.name,
        previewUrl: "",
        fileType: doc.file_type ?? "PDF",
      })),
    [data?.reference_documents]
  );

  const pins: DocumentPin[] = useMemo(
    () =>
      requestedAnswers.flatMap((a, index) => {
        if (
          a.document_index == null ||
          a.x_position == null ||
          a.y_position == null
        )
          return [];
        return [
          {
            id: a.uuid,
            docIndex: a.document_index,
            x: a.x_position,
            y: a.y_position,
            pageNumber: a.page_number ?? 1,
            number: index + 1,
            isActive: activePinId === a.uuid,
          },
        ];
      }),
    [requestedAnswers, activePinId]
  );

  const sidebarQuestions: SidebarQuestion[] = useMemo(
    () =>
      requestedAnswers.map((a) => ({
        id: a.uuid,
        text: a.question,
        docIndex: a.document_index ?? null,
        x: a.x_position ?? null,
        y: a.y_position ?? null,
      })),
    [requestedAnswers]
  );

  const handleQuestionClick = useCallback(
    (id: string) => {
      const answer = requestedAnswers.find((a) => a.uuid === id);
      if (answer?.document_index != null) {
        setActiveDocIndex(answer.document_index);
      }
      setActivePinId(id);
      setTimeout(() => setActivePinId(null), 1200);
    },
    [requestedAnswers]
  );

  const handleViewDocument = useCallback(
    (id: string) => {
      const answer = requestedAnswers.find((a) => a.uuid === id);
      if (answer?.document_index != null) {
        setActiveDocIndex(answer.document_index);
      }
      setActivePinId(id);
      setDocViewerQuestionId(id);
    },
    [requestedAnswers]
  );

  const closeDocViewer = useCallback(() => {
    setDocViewerQuestionId(null);
    setActivePinId(null);
  }, []);

  const handleDecision = useCallback(
    async (
      uuid: string,
      decision: RequestedAnswerReviewDecision,
      followUpNote?: string
    ) => {
      setSavingDecisionKey(`${uuid}:${decision}`);
      try {
        await saveReviewProgress({
          requestedAnswerId: uuid,
          groupId,
          decision,
          followUpNote,
        }).unwrap();
      } catch (error) {
        alertToast({
          message:
            (error as BackendError)?.data?.error?.message || (error as string),
        });
      } finally {
        setSavingDecisionKey(null);
      }
    },
    [groupId, saveReviewProgress, alertToast]
  );

  const debouncedSaveFollowUpNote = useMemo(
    () =>
      debounce((uuid: string, note: string) =>
        handleDecision(
          uuid,
          REQUESTED_ANSWER_REVIEW_STATUS.FOLLOW_UP_REQUESTED,
          note
        )
      ),
    [handleDecision]
  );

  const handleSaveAndClose = () => {
    successToast({ message: "Review progress saved" });
  };

  const handleAcceptAll = async () => {
    await Promise.allSettled(
      pendingReviewAnswers.map(({ uuid }) =>
        handleDecision(uuid, REQUESTED_ANSWER_REVIEW_STATUS.ACCEPTED)
      )
    );
  };

  const handleCompleteReview = async () => {
    setHasAttemptedReview(true);
    const firstUnresolved = requestedAnswers.find(isQuestionUnresolved);
    if (firstUnresolved) {
      setScrollToQuestionId(null);
      requestAnimationFrame(() => setScrollToQuestionId(firstUnresolved.uuid));
      if (firstUnresolved.document_index != null) {
        setActiveDocIndex(firstUnresolved.document_index);
      }
      return;
    }
    setScrollToQuestionId(null);

    try {
      await reviewAnswers({
        requestId,
        groupId,
        reviews: requestedAnswers.map(
          ({ uuid, review_status, follow_up_note }) => ({
            uuid,
            decision: review_status as RequestedAnswerReviewDecision,
            followUpNote: follow_up_note ?? undefined,
          })
        ),
      }).unwrap();
      dispatch(taskApis.util.invalidateTags(["Tasks"]));
      successToast({
        message: hasFollowUpRequested
          ? "Follow-up requested"
          : "Review submitted",
      });
      close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message || (error as string),
      });
    }
  };

  const handleFollowUpNoteInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const renderQuestionExtra = useCallback(
    (question: SidebarQuestion) => {
      const answer = requestedAnswers.find((a) => a.uuid === question.id);
      const decision = answer?.review_status;
      const isFollowUpSelected =
        decision === REQUESTED_ANSWER_REVIEW_STATUS.FOLLOW_UP_REQUESTED;
      const isAcceptedSelected =
        decision === REQUESTED_ANSWER_REVIEW_STATUS.ACCEPTED;

      const answerDocuments = answer?.documents ?? [];
      const isAcceptSaving =
        savingDecisionKey ===
        `${question.id}:${REQUESTED_ANSWER_REVIEW_STATUS.ACCEPTED}`;
      const isFollowUpSaving =
        savingDecisionKey ===
        `${question.id}:${REQUESTED_ANSWER_REVIEW_STATUS.FOLLOW_UP_REQUESTED}`;
      const isSavingThisQuestion = isAcceptSaving || isFollowUpSaving;

      return (
        <div className="t-flex t-flex-col t-gap-2">
          <div className="t-flex t-flex-col t-gap-1.5 t-pl-2 t-border-0 t-border-l-2 t-border-solid t-border-neutral-20">
            {answer?.answer && (
              <p className="t-m-0 t-text-body-sm t-text-text-100">
                {answer.answer}
              </p>
            )}
            {answerDocuments.map((doc) => (
              <FilePreviewCell
                key={doc.uuid}
                file={{
                  ...doc,
                  uuid: doc.file_uuid,
                  file_type: doc.file_type as FileObject["file_type"],
                }}
              />
            ))}
            {!answer?.answer && answerDocuments.length === 0 && (
              <p className="t-m-0 t-text-body-sm t-text-text-100">-</p>
            )}
          </div>

          <div className="t-mb-1">
            <PreviousResponses rounds={answer?.previous_rounds ?? []} />
          </div>

          {isAcceptedSelected && (
            <div className="t-flex t-items-center t-gap-3">
              <Tag tagType="green" rounded icon={false}>
                Reviewed and accepted
              </Tag>
              <Button
                type="button"
                customType="link_neutral"
                size="small"
                className="t-text-text-30 t-text-body-sm"
                isLoading={isFollowUpSaving}
                disabled={isSavingThisQuestion && !isFollowUpSaving}
                onClick={() =>
                  handleDecision(
                    question.id,
                    REQUESTED_ANSWER_REVIEW_STATUS.FOLLOW_UP_REQUESTED
                  )
                }
              >
                Reopen & follow-up
              </Button>
            </div>
          )}

          {isFollowUpSelected && (
            <div className="t-flex t-flex-col t-gap-1.5">
              <div onClick={(e) => e.stopPropagation()}>
                <FormikForm
                  initialValues={{ note: answer?.follow_up_note ?? "" }}
                  onSubmit={() => {}}
                >
                  {() => (
                    <TextArea
                      name="note"
                      placeholder={REVIEW_NOTE_PLACEHOLDER}
                      rows={2}
                      size="small"
                      autoResize={false}
                      style={{ minHeight: "3.5rem", overflowY: "hidden" }}
                      onInput={handleFollowUpNoteInput}
                      onChange={(e) =>
                        debouncedSaveFollowUpNote(question.id, e.target.value)
                      }
                    />
                  )}
                </FormikForm>
              </div>
              <Button
                type="button"
                customType="link_neutral"
                size="small"
                className="t-text-neutral-60 t-text-body-sm"
                isLoading={isAcceptSaving}
                disabled={isSavingThisQuestion && !isAcceptSaving}
                onClick={() =>
                  handleDecision(
                    question.id,
                    REQUESTED_ANSWER_REVIEW_STATUS.ACCEPTED
                  )
                }
              >
                Accept response instead
              </Button>
            </div>
          )}

          {!isAcceptedSelected && !isFollowUpSelected && (
            <div className="t-flex t-items-center t-gap-3">
              <Button
                type="button"
                customType="secondary"
                size="small"
                leftIcon={
                  <span className="t-text-green-80">
                    <TickSlim />
                  </span>
                }
                isLoading={isAcceptSaving}
                disabled={isSavingThisQuestion && !isAcceptSaving}
                onClick={() =>
                  handleDecision(
                    question.id,
                    REQUESTED_ANSWER_REVIEW_STATUS.ACCEPTED
                  )
                }
              >
                Looks good
              </Button>
              <Button
                type="button"
                customType="ghost"
                size="small"
                leftIcon={<Thread />}
                isLoading={isFollowUpSaving}
                disabled={isSavingThisQuestion && !isFollowUpSaving}
                onClick={() =>
                  handleDecision(
                    question.id,
                    REQUESTED_ANSWER_REVIEW_STATUS.FOLLOW_UP_REQUESTED
                  )
                }
              >
                Ask a follow-up
              </Button>
            </div>
          )}
        </div>
      );
    },
    [
      requestedAnswers,
      handleDecision,
      debouncedSaveFollowUpNote,
      savingDecisionKey,
    ]
  );

  const hasAnnotationDocs = annotationDocs.length > 0;
  const footerButtonClassName = "sm:t-flex-1";

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content
        size={hasAnnotationDocs ? "xxxl" : "xl"}
        className={classNames({ "t-h-[90vh]": hasAnnotationDocs })}
        onEscapeKeyDown={(e) => e.preventDefault()}
        useCustomOverlay
      >
        <Async.Root
          isLoading={isLoading}
          isEmpty={requestedAnswers.length === 0}
          isSuccess={isSuccess}
        >
          <Async.Empty>
            <Modal.Header>
              <Modal.Title>Review Information</Modal.Title>
              <Modal.Close type="button" />
            </Modal.Header>
            <Modal.Body>
              <EmptyScreen text="No Data Found" />
            </Modal.Body>
          </Async.Empty>

          <Async.Success>
            <Modal.Header>
              <Modal.Title>Review Information</Modal.Title>
              <Modal.Close type="button" />
            </Modal.Header>

            <Modal.Body
              className={classNames("t-flex-1 t-min-h-0", {
                "!t-p-0 !t-overflow-hidden": hasAnnotationDocs,
                "!t-p-4": !hasAnnotationDocs,
              })}
            >
              {hasAnnotationDocs ? (
                <div className="t-relative t-flex t-flex-row t-overflow-hidden t-h-full">
                  {!isSmallScreen && (
                    <div className="t-overflow-auto t-h-full t-flex-1">
                      <DocumentAnnotationPanel
                        docs={annotationDocs}
                        activeDocIndex={activeDocIndex}
                        onTabChange={setActiveDocIndex}
                        pins={pins}
                        onPinClick={handleQuestionClick}
                        groupId={groupId}
                      />
                    </div>
                  )}

                  <div
                    className={classNames(
                      "t-h-full t-overflow-hidden",
                      isSmallScreen
                        ? "t-w-full"
                        : "t-w-[550px] t-shrink-0 t-border-0 t-border-l t-border-solid t-border-neutral-10"
                    )}
                  >
                    <QuestionSidebar
                      questions={sidebarQuestions}
                      activePinId={activePinId}
                      docs={annotationDocs}
                      variant="answer"
                      showHeader={true}
                      headerSubtext={
                        pendingReviewAnswers.length > 0
                          ? `${pluralize(
                              pendingReviewAnswers.length,
                              "answer",
                              "answers"
                            )} left to review`
                          : undefined
                      }
                      headerAction={
                        pendingReviewAnswers.length > 0 ? (
                          <Button
                            type="button"
                            customType="link_neutral"
                            size="small"
                            onClick={() => void handleAcceptAll()}
                          >
                            Accept all
                          </Button>
                        ) : undefined
                      }
                      onQuestionClick={handleQuestionClick}
                      onViewDocument={
                        isSmallScreen ? handleViewDocument : undefined
                      }
                      renderQuestionExtra={renderQuestionExtra}
                      errorQuestionIds={errorQuestionIds}
                      errorText={REVIEW_PENDING_ERROR}
                      scrollToQuestionId={scrollToQuestionId}
                      bottomContent={
                        <AcceptedResponses
                          responses={data?.accepted_responses ?? []}
                          startNumber={sidebarQuestions.length + 1}
                        />
                      }
                    />
                  </div>

                  {isSmallScreen && docViewerQuestionId && (
                    <div className="t-absolute t-inset-0 t-z-10 t-flex t-flex-col t-bg-surface">
                      <div className="t-flex t-h-12 t-shrink-0 t-items-center t-gap-2 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-px-3">
                        <Button
                          type="button"
                          customType="ghost_icon"
                          size="small"
                          onClick={closeDocViewer}
                          aria-label="Back to questions"
                        >
                          <CaretLeft />
                        </Button>
                        <span className="t-text-subtext-sm t-text-text-60">
                          Back to questions
                        </span>
                      </div>
                      <div className="t-min-h-0 t-flex-1 t-overflow-auto">
                        <DocumentAnnotationPanel
                          docs={annotationDocs}
                          activeDocIndex={activeDocIndex}
                          onTabChange={setActiveDocIndex}
                          pins={pins}
                          onPinClick={handleQuestionClick}
                          groupId={groupId}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <QuestionSidebar
                  questions={sidebarQuestions}
                  activePinId={activePinId}
                  docs={annotationDocs}
                  variant="answer"
                  showHeader={false}
                  compact
                  onQuestionClick={handleQuestionClick}
                  renderQuestionExtra={renderQuestionExtra}
                  errorQuestionIds={errorQuestionIds}
                  errorText={REVIEW_PENDING_ERROR}
                  scrollToQuestionId={scrollToQuestionId}
                  bottomContent={
                    <AcceptedResponses
                      responses={data?.accepted_responses ?? []}
                      startNumber={sidebarQuestions.length + 1}
                    />
                  }
                />
              )}
            </Modal.Body>

            <Modal.Footer>
              <div className="t-flex t-items-center t-justify-between t-gap-3 sm:t-flex-col sm:t-items-stretch">
                {data?.submitted_by && (
                  <div className="t-flex t-items-center t-gap-1 t-text-text-30 t-text-body-sm">
                    <Info className="t-size-3 t-shrink-0" />
                    <div className="t-italic">
                      Answer submitted by {data.submitted_by} on{" "}
                      {dayjs(data.submitted_at).format(DISPLAY_DATE)}
                    </div>
                  </div>
                )}

                <div className="t-flex t-shrink-0 t-items-center t-justify-end t-gap-3 sm:t-flex-wrap">
                  <Modal.RawClose asChild>
                    <Button
                      type="button"
                      onClick={handleSaveAndClose}
                      className={footerButtonClassName}
                    >
                      Save & Close
                    </Button>
                  </Modal.RawClose>
                  <Button
                    type="button"
                    customType="primary"
                    className={footerButtonClassName}
                    onClick={handleCompleteReview}
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  >
                    {hasFollowUpRequested
                      ? "Complete review & send follow-up"
                      : "Complete review"}
                  </Button>
                </div>
              </div>
            </Modal.Footer>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
